import { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDialogCloseGuard } from '@/hooks/useDialogCloseGuard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Loader2, Sparkles } from 'lucide-react';
import { releaseSchema, type ReleaseFormData } from '../schemas';
import {
  ReleaseNoteCategory,
  ReleaseNotePlatform,
  CATEGORY_LABELS,
  PLATFORM_LABELS,
  type ReleaseDto,
} from '../types';

interface ReleaseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ReleaseFormData) => Promise<void>;
  release?: ReleaseDto | null;
  isSubmitting: boolean;
}

export function ReleaseFormDialog({
  open,
  onOpenChange,
  onSubmit,
  release,
  isSubmitting,
}: ReleaseFormDialogProps) {
  const isEditing = !!release;

  const form = useForm<ReleaseFormData>({
    resolver: zodResolver(releaseSchema),
    defaultValues: {
      webVersion: '',
      mobileVersion: '',
      title: '',
      summary: null,
      releaseDate: new Date().toISOString().split('T')[0],
      isPublished: false,
      notes: [],
      highlights: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'notes',
  });

  const {
    fields: highlightFields,
    append: appendHighlight,
    remove: removeHighlight,
  } = useFieldArray({
    control: form.control,
    name: 'highlights',
  });

  useEffect(() => {
    if (open && release) {
      form.reset({
        webVersion: release.webVersion,
        mobileVersion: release.mobileVersion,
        title: release.title,
        summary: release.summary,
        releaseDate: release.releaseDate.split('T')[0],
        isPublished: release.isPublished,
        notes: release.notes.map((n) => ({
          category: n.category,
          platform: n.platform,
          description: n.description,
          sortOrder: n.sortOrder,
        })),
        highlights: release.highlights.map((h) => ({
          moduleName: h.moduleName,
          content: h.content,
          sortOrder: h.sortOrder,
        })),
      });
    } else if (open && !release) {
      form.reset({
        webVersion: '',
        mobileVersion: '',
        title: '',
        summary: null,
        releaseDate: new Date().toISOString().split('T')[0],
        isPublished: false,
        notes: [],
        highlights: [],
      });
    }
  }, [open, release, form]);

  const handleSubmit = form.handleSubmit(async (data) => {
    await onSubmit(data);
  });

  // Background click is ignored; escape / X / cancel prompt when there are unsaved changes.
  const { handleOpenChange, preventOutsideClose } = useDialogCloseGuard({
    isDirty: form.formState.isDirty,
    onClose: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={preventOutsideClose}
        onInteractOutside={preventOutsideClose}
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Release' : 'Create Release'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="webVersion"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Web Version *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 0.5.0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mobileVersion"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile Version *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 1.4.0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="releaseDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Release Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. From Dock to Desktop"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="summary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Summary</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional summary of this release..."
                      rows={2}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isPublished"
              render={({ field }) => (
                <FormItem className="flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">
                    {field.value ? 'Published' : 'Draft'}
                  </FormLabel>
                </FormItem>
              )}
            />

            <Separator />

            {/* Highlights Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Highlights
                  <span className="text-muted-foreground font-normal">
                    (announcement blocks for new modules)
                  </span>
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    appendHighlight({
                      moduleName: '',
                      content: '',
                      sortOrder: highlightFields.length,
                    })
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Highlight
                </Button>
              </div>

              {highlightFields.length === 0 && (
                <p className="text-sm text-muted-foreground italic py-2 text-center">
                  No highlights. Add one to feature a new module in
                  announcement style.
                </p>
              )}

              <div className="space-y-3">
                {highlightFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="border rounded-lg p-3 space-y-3 bg-muted/30"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-2">
                        <label className="text-xs font-medium">
                          Module Name
                        </label>
                        <Controller
                          control={form.control}
                          name={`highlights.${index}.moduleName` as const}
                          render={({ field: nameField }) => (
                            <Input
                              placeholder="e.g. Receiving Module"
                              {...nameField}
                            />
                          )}
                        />
                        {form.formState.errors.highlights?.[index]
                          ?.moduleName && (
                          <p className="text-sm font-medium text-destructive">
                            {
                              form.formState.errors.highlights[index].moduleName
                                ?.message
                            }
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-5 shrink-0"
                        onClick={() => removeHighlight(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-medium">
                        Content (Markdown)
                      </label>
                      <Controller
                        control={form.control}
                        name={`highlights.${index}.content` as const}
                        render={({ field: contentField }) => (
                          <Textarea
                            placeholder="Write announcement content in Markdown..."
                            rows={4}
                            {...contentField}
                          />
                        )}
                      />
                      {form.formState.errors.highlights?.[index]?.content && (
                        <p className="text-sm font-medium text-destructive">
                          {
                            form.formState.errors.highlights[index].content
                              ?.message
                          }
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {highlightFields.length > 0 && (
                <div className="flex justify-center mt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() =>
                      appendHighlight({
                        moduleName: '',
                        content: '',
                        sortOrder: highlightFields.length,
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Highlight
                  </Button>
                </div>
              )}
            </div>

            <Separator />

            {/* Change Items Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Change Items</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    append({
                      category: ReleaseNoteCategory.Feature,
                      platform: ReleaseNotePlatform.All,
                      description: '',
                      sortOrder: fields.length,
                    })
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>

              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground italic py-4 text-center">
                  No change items yet. Click "Add Item" to add one.
                </p>
              )}

              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="border rounded-lg p-3 space-y-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid grid-cols-2 gap-3 flex-1">
                        <div className="space-y-2">
                          <label className="text-xs font-medium">
                            Category
                          </label>
                          <Controller
                            control={form.control}
                            name={`notes.${index}.category` as const}
                            render={({ field: catField }) => (
                              <Select
                                value={String(catField.value)}
                                onValueChange={(v) =>
                                  catField.onChange(Number(v))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(CATEGORY_LABELS).map(
                                    ([value, label]) => (
                                      <SelectItem
                                        key={value}
                                        value={value}
                                      >
                                        {label}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {form.formState.errors.notes?.[index]?.category && (
                            <p className="text-sm font-medium text-destructive">
                              {form.formState.errors.notes[index].category?.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium">
                            Platform
                          </label>
                          <Controller
                            control={form.control}
                            name={`notes.${index}.platform` as const}
                            render={({ field: platField }) => (
                              <Select
                                value={String(platField.value)}
                                onValueChange={(v) =>
                                  platField.onChange(Number(v))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(PLATFORM_LABELS).map(
                                    ([value, label]) => (
                                      <SelectItem
                                        key={value}
                                        value={value}
                                      >
                                        {label}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {form.formState.errors.notes?.[index]?.platform && (
                            <p className="text-sm font-medium text-destructive">
                              {form.formState.errors.notes[index].platform?.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-5 shrink-0"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Controller
                        control={form.control}
                        name={`notes.${index}.description` as const}
                        render={({ field: descField }) => (
                          <Textarea
                            placeholder="Describe the change..."
                            rows={2}
                            {...descField}
                          />
                        )}
                      />
                      {form.formState.errors.notes?.[index]?.description && (
                        <p className="text-sm font-medium text-destructive">
                          {form.formState.errors.notes[index].description?.message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {fields.length > 0 && (
                <div className="flex justify-center mt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() =>
                      append({
                        category: ReleaseNoteCategory.Feature,
                        platform: ReleaseNotePlatform.All,
                        description: '',
                        sortOrder: fields.length,
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
